import { XMarkIcon, DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { deleteFile } from "../actions/sharedActions";
import { useAppDispatch } from "../store";
import { toast } from "sonner";
import log from "../log";

// Helper function to check if a file type is previewable
const isPreviewableFile = (contentType: string): boolean => {
  return (
    contentType.startsWith("image/") ||
    contentType === "application/pdf" ||
    isTextFile(contentType)
  );
};

// Helper function to check if a file is a text file
export const isTextFile = (contentType: string): boolean => {
  return (
    contentType === "text/plain" ||
    contentType === "text/markdown" ||
    contentType === "text/csv" ||
    contentType === "text/html" ||
    contentType === "application/json" ||
    contentType === "text/javascript" ||
    contentType === "text/css" ||
    contentType === "application/xml" ||
    contentType === "text/xml"
  );
};

export interface FileAttachment {
  file: File;
  type: string;
  content_type: string;
  preview: string;
  id?: string; // Server-assigned ID
  temporary_id: string; // Client-side temporary ID
  status: "uploading" | "success" | "error";
  error?: string;
  uploadController?: AbortController; // Add this to handle cancellation
}

interface AttachmentsProps {
  attachments: FileAttachment[];
  setAttachments: (
    attachments:
      | FileAttachment[]
      | ((attachments: FileAttachment[]) => FileAttachment[])
  ) => void;
  disabled: boolean;
  isAudioMode: boolean;
  isDragging: boolean;
  onPreviewFile: (file: {
    url: string;
    contentType: string;
    type: string;
    name: string;
  }) => void;
}

export function Attachments({
  attachments,
  setAttachments,
  disabled,
  isAudioMode,
  isDragging,
  onPreviewFile,
}: AttachmentsProps) {
  const dispatch = useAppDispatch();

  // Remove an attachment by temporary ID
  const removeAttachment = async (temporaryId: string) => {
    // Find the attachment by temporary ID
    const attachmentIndex = attachments.findIndex(
      (attachment) => attachment.temporary_id === temporaryId
    );

    if (attachmentIndex === -1) return;

    const attachment = attachments[attachmentIndex];

    // Cancel the upload if it's in progress
    if (attachment.status === "uploading" && attachment.uploadController) {
      attachment.uploadController.abort();
      console.log(`Aborted upload for ${attachment.file.name}`);

      // Remove from UI immediately for uploads in progress
      setAttachments((prev) => {
        // Revoke the URL to prevent memory leaks
        URL.revokeObjectURL(attachment.preview);
        // Filter out the attachment with the matching temporary ID
        return prev.filter((a) => a.temporary_id !== temporaryId);
      });

      // No need for server deletion since upload was aborted
      return;
    }

    // If the file was successfully uploaded, delete it from the server
    if (attachment.status === "success" && attachment.id) {
      toast.promise(
        async () => {
          try {
            await dispatch(deleteFile({ fileId: attachment.id! })).unwrap();
          } catch (error) {
            // Rethrow to be caught by toast.promise
            throw error;
          }
        },
        {
          loading: `Removing ${attachment.file.name}...`,
          success: `${attachment.file.name} removed successfully`,
          error: (err) => {
            const errorMessage =
              err instanceof Error ? err.message : "Unknown error";
            return `Failed to remove ${attachment.file.name}: ${errorMessage}`;
          },
        }
      );
    }

    // For error states or after server deletion, clean up UI
    setAttachments((prev) => {
      // Revoke the URL to prevent memory leaks
      URL.revokeObjectURL(attachment.preview);
      // Filter out the attachment with the matching temporary ID
      return prev.filter((a) => a.temporary_id !== temporaryId);
    });
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      attachments.forEach((attachment) => {
        URL.revokeObjectURL(attachment.preview);
      });
    };
  }, []);

  if ((attachments.length === 0 && !isDragging) || isAudioMode) {
    return null;
  }
  log.debug("ATTACHMENTS", attachments);
  return (
    <div className="flex flex-wrap gap-2 p-3 border-b border-slate-200 rounded-t-[24px] overflow-hidden">
      {attachments.map((attachment) => (
        <div key={attachment.temporary_id} className="relative">
          <div
            className={`w-16 h-16 rounded bg-slate-100 flex items-center justify-center overflow-hidden border ${
              attachment.status === "uploading"
                ? "border-blue-200"
                : attachment.status === "error"
                  ? "border-red-200"
                  : "border-slate-200"
            } ${isPreviewableFile(attachment.content_type) ? "cursor-pointer hover:opacity-90" : ""}`}
            onClick={() =>
              attachment.status === "success" &&
              isPreviewableFile(attachment.content_type) &&
              onPreviewFile({
                url: attachment.preview,
                contentType: attachment.content_type,
                type: attachment.type,
                name: attachment.file.name,
              })
            }
          >
            {attachment.content_type.startsWith("image/") ? (
              <img
                src={attachment.preview}
                alt={attachment.file.name}
                className={`max-w-full max-h-full object-contain ${attachment.status === "uploading" ? "opacity-50" : ""}`}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full w-full">
                <div
                  className={`text-xs ${attachment.status === "error" ? "text-red-500" : "text-chat-text-light"}`}
                >
                  {attachment.file.name.split(".").pop()?.toUpperCase() ||
                    "FILE"}
                </div>
                <div className="text-[8px] text-center text-chat-text-light mt-1 truncate w-full px-1">
                  {attachment.file.name.length > 15
                    ? `${attachment.file.name.substring(0, 12)}...`
                    : attachment.file.name}
                </div>
                {attachment.status === "uploading" && (
                  <div className="flex flex-col items-center">
                    <svg
                      className="animate-spin h-3 w-3 text-blue-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <div className="text-[8px] text-center text-blue-500 font-medium mt-1">
                      Uploading...
                    </div>
                  </div>
                )}
                {attachment.status === "error" && (
                  <div className="text-[8px] text-center text-red-500 font-medium">
                    Upload failed
                  </div>
                )}
                {attachment.status === "success" &&
                  isPreviewableFile(attachment.content_type) && (
                    <div className="text-[8px] text-center text-blue-500 font-medium">
                      Preview
                    </div>
                  )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => removeAttachment(attachment.temporary_id)}
            className="absolute -top-2 -right-2 bg-white rounded-full p-0.5 shadow-sm text-chat-text-light hover:text-red-500"
            aria-label="Remove attachment"
            disabled={disabled}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ))}

      {/* Drag & Drop Overlay */}
      {isDragging && !isAudioMode && (
        <div className="absolute inset-0 bg-white bg-opacity-80 backdrop-blur-sm rounded-[24px] flex items-center justify-center z-10 border-2 border-dashed border-brand">
          <div className="text-center p-4">
            <DocumentArrowUpIcon className="h-10 w-10 mx-auto mb-2 text-brand" />
            <p className="text-brand font-medium">Drop files here</p>
          </div>
        </div>
      )}
    </div>
  );
}
